import React, {useState, useEffect} from "react";
import {
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  Alert,
  Grid,
  Paper,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Dialog
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {useCustomer} from "context/customer/customerContext";
import {useNavigate} from "react-router-dom";

import {ChargeList, ChargeItem, ChargeTitle, ChargeItemAmount} from "../styles";
import {DialogInput} from "../../../../pages/Dealers/Inspection/Condition/styles";
import {useUI} from "../../../../context/ui/uiContext";

const ChargeAmount = styled(Typography)({
  fontSize: "84px",
  fontWeight: "bold",
  margin: "40px 0",
  textAlign: "center"
});

const Title = styled(Typography)({
  fontSize: "34px",
  fontWeight: "bold",
  margin: "40px 0",
  textAlign: "center"
});

/**
 * EstimatedCharge component displays the estimated charges for the user's selected delivery or pickup option.
 * If the user selects a pickup option, a POA dialog is shown before submitting.
 * @component
 * @returns {JSX.Element} The EstimatedCharge component.
 * @description
 * This component allows users to review their estimated charges and agree to the terms before submitting.
 * If the user selects the "pickup" option, a dialog for confirming the customer's POA details is shown.
 * @example
 * <EstimatedCharge />
 * @function
 * @param {object} root0 - Root object
 * @param {Function} root0.setStep - Function to change the step of the process
 */
function EstimatedCharge() {
  const [agreed, setAgreed] = useState(false);
  const [openNameEmailDialog, setOpenNameEmailDialog] = useState(false);
  const [poaSigner1, setPoaSigner1] = useState("");
  const [poaSigner1Email, setPoaSigner1Email] = useState("");
  const [openDeclineDialog, setOpenDeclineDialog] = useState(false);
  const {showAlert} = useUI();

  const {charges, deliveryOption, fetchTotalCharges, customerDetails, EWTChargesDecision, submitPOADetails} = useCustomer();

  useEffect(() => {
    fetchTotalCharges();
  }, []);

  const {
    pickupCharge,
    selectedItemCharges,
    totalSelectedItemCharge,
    dispositionCharge,
    estimatedMileageCharge,
    totalActualChargeIncludingPickup
  } = charges || {};



  const navigate = useNavigate();

  const handleAccept = async () => {
    if (deliveryOption === "pickup") {
      setOpenNameEmailDialog(true);
    } else if (agreed) {
      await EWTChargesDecision({
        ewtChargesAccepted: true,
        ewtCharges: totalActualChargeIncludingPickup,
        poaSigner: poaSigner1 || customerDetails?.customerName,
        customerName: customerDetails?.customerName
      });
      navigate("/customer/status");
    } else {
      // Optionally show an error or prompt to the user to agree to the terms.
    }
  };

  const handleDecline = () => {
    setOpenDeclineDialog(true); // Open the decline confirmation dialog
  };

  const handleDeclineConfirm = async () => {
    await EWTChargesDecision({
      ewtChargesAccepted: false,
      ewtCharges: totalActualChargeIncludingPickup,
      poaSigner: poaSigner1 || customerDetails?.customerName
    });
    navigate("/");
  };

  const handlePOASubmit = async () => {
    // Logic for confirming POA details and navigating to the next step


    if (!poaSigner1Email) {
      showAlert("POA email address is required", "error");
      return;
    }
    setOpenNameEmailDialog(false);
    if (agreed) {
      await EWTChargesDecision({
        ewtChargesAccepted: true,
        ewtCharges: totalActualChargeIncludingPickup,
        poaSigner: poaSigner1 || customerDetails?.customerName,
      });

      await submitPOADetails({
        "poaSigner1": customerDetails?.customerName,
        "poaSigner1Email": poaSigner1Email,
      });
      navigate("/customer/status");
    }
  };

  if (!totalActualChargeIncludingPickup) {
    return null;
  }

  return (
    <Grid container spacing={2} justifyContent="center" alignItems="start" sx={{
      margin: "25px 0",
      minWidth: {
        xs: "100%", // Extra-small screens
        sm: "400px", // Small screens
        md: "600px", // Medium screens
        lg: "800px", // Large screens
      },
    }} >
      <Paper elevation={3} style={{ padding: "15px", margin: "0 0", maxWidth: "600px", width: "100%" }}>
        <Title>Estimated turn-in charge</Title>
        <ChargeAmount variant="h1">${totalActualChargeIncludingPickup?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</ChargeAmount>

        <ChargeList>
          {selectedItemCharges && selectedItemCharges?.length > 0 ? selectedItemCharges.map((chargeItem, index) => (
            <ChargeItem key={index}>
              {chargeItem.options && chargeItem.options.length > 0 && chargeItem.options.map((optionItem, optionIndex) => (
                <div key={optionIndex} style={{marginLeft: "20px"}}>
                  <ChargeTitle>{optionItem.option}</ChargeTitle>
                  <ChargeItemAmount>Charge: ${optionItem.charge.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}</ChargeItemAmount>
                </div>
              ))}
            </ChargeItem>
          )) : <div style={{marginLeft: "20px"}}>
            <ChargeTitle>EWT Charges</ChargeTitle>
            <ChargeItemAmount>Charge: $0.00</ChargeItemAmount>
          </div>}

          {pickupCharge !== null ? <ChargeItem>
            <div style={{marginLeft: "20px"}}>
              <ChargeTitle>Pickup Charge</ChargeTitle>
              <ChargeItemAmount>Charge: ${pickupCharge.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2 })}</ChargeItemAmount>
            </div>
          </ChargeItem> : null}

          {dispositionCharge ? <ChargeItem>
            <div style={{ marginLeft: "20px" }}>
              <ChargeTitle>Disposition Charge</ChargeTitle>
              <ChargeItemAmount>Charge: ${dispositionCharge.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</ChargeItemAmount>
            </div>
          </ChargeItem> : null}

          {estimatedMileageCharge !== null ? <ChargeItem>
            <div style={{ marginLeft: "20px" }}>
              <ChargeTitle>Estimated Mileage Charge</ChargeTitle>
              <ChargeItemAmount>Charge: ${estimatedMileageCharge.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</ChargeItemAmount>
            </div>
          </ChargeItem> : null}


        </ChargeList>

        <Alert
          severity="info"
          variant="outlined"
          sx={{ borderRadius: "8px", mt: 3, mb: 3 }} // Adjust if necessary
        >
          {deliveryOption === "dropOff" ?
            <Typography variant="body2" color="textSecondary">
            This is the estimated amount owed based on the completed pre-inspection report.  All charges, including your turn-in disposition fee, will be based on the final, dealer inspection to be conducted by your H-D Dealership at time of bike turn-in.  All charges will be applied to your HDFS account upon final dealer submission.   You will receive your official Flex Financing End of Loan statement by mail approximately 14 days after return detailing any charges incurred for mileage overages and/or excess wear and tear.
            </Typography>
            : <Typography variant="body2" color="textSecondary">
            This is the amount owed based on the completed pre-inspection report.  All charges will be applied to your HDFS account upon final submission.  You will receive your official Flex Financing End of Loan statement by mail approximately 14 days after return detailing any charges incurred for mileage overages and/or excess wear and tear.
            </Typography>}
        </Alert>


        <FormControlLabel
          control={
            <Checkbox
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              name="agreedToTerms"
              color="primary"
            />
          }
          label={
            deliveryOption === "dropOff" ?
              <Typography>
                I understand that the total amount owed is an estimate based on the completed inspection report and that all final charges will be based on the final, dealer inspection to be conducted by my preferred H-D Dealership at time of bike turn-in.
              </Typography> : <Typography>
                I agree to the amount owed based on the completed inspection report and understand that all amounts owed will be applied to my HDFS account.
              </Typography>
          }
        />

        <Button variant="contained" color="success" disabled={!agreed} fullWidth style={{ marginTop: "20px", color: "white" }} onClick={handleAccept}>
          Accept
        </Button>

        <Button variant="contained" color="error" fullWidth style={{ marginTop: "20px" }} onClick={handleDecline}>
          Decline
        </Button>
      </Paper>

      <Dialog
        open={openNameEmailDialog}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            setOpenNameEmailDialog(false);
          }
        }}
        disableEscapeKeyDown
      >
        <DialogTitle>Confirm Details</DialogTitle>
        <DialogContent>
          <DialogContentText>
            A signed Power of Attorney is required to complete the title process.  Please confirm your email address before proceeding to receive and sign an electronic POA to complete the turn-in process. Turn-in cannot be completed without successful completion of the POA.  If there are questions, please contact a Titles Specialist at 866-433-7562.
          </DialogContentText>
          <DialogInput disabled margin="dense" size="small" label="Customer Name" type="text" fullWidth value={poaSigner1 || customerDetails?.customerName} onChange={(e) => setPoaSigner1(e.target.value)} />
          <DialogInput margin="dense" size="small" label="Customer Email" type="email" fullWidth value={poaSigner1Email} onChange={(e) => setPoaSigner1Email(e.target.value)} />
          {/*{vehicle?.poaSigner2 && (*/}
          {/*  <>*/}
          {/*    <DialogInput disabled margin="dense" size="small" label="Customer Name" type="text" fullWidth value={poaSigner2} onChange={(e) => setPoaSigner2(e.target.value)} />*/}
          {/*    <DialogInput margin="dense" size="small" label="Customer Email" type="email" fullWidth value={poaSigner2Email} onChange={(e) => setPoaSigner2Email(e.target.value)} />*/}
          {/*  </>*/}
          {/*)}*/}
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePOASubmit} color="primary" autoFocus>Confirm</Button>
        </DialogActions>
      </Dialog>

      {/* Decline Confirmation Dialog */}
      <Dialog
        open={openDeclineDialog}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            setOpenDeclineDialog(false);
          }
        }}
        disableEscapeKeyDown
      >
        <DialogTitle>Confirm Decline</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your turn-in process will be ended. Are you sure you want to decline?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeclineDialog(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeclineConfirm} color="error">
            Confirm Decline
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}

export default EstimatedCharge;
